"use client";

import { SecondaryNavigation, SecondaryNavigationVariant } from "../types";
import { FC, useRef } from "react";
import cn from "classnames";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { getDataAutomationGroup, getDataLinkAttributes } from "@/helpers";
import Link from "@/components/Link";
import { Button } from "@/components/Button";
import { CaretDownSolid } from "@/icons";

export type DropdownType = {
  dropdown: NonNullable<SecondaryNavigation["dropdowns"]>[0];
  variant?: SecondaryNavigationVariant;
  onOpenChange?: (open: boolean) => void;
};

const Dropdown: FC<DropdownType> = ({
  dropdown,
  variant = "default",
  onOpenChange,
}) => {
  const columns = dropdown.columns ?? [];
  const hasHeadline = columns.some((column) => column.title !== undefined);
  const prevOpenRef = useRef<boolean>(false);
  const isRedesigned = variant === "redesigned";

  return (
    <Popover as="li">
      {({ open }) => {
        if (open !== prevOpenRef.current) {
          prevOpenRef.current = open;
          onOpenChange?.(open);
        }

        return (
          <>
            <PopoverButton
              className={
                isRedesigned
                  ? cn(
                      "text-link-xs text-swdc-black-600 data-focus:outline-swdc-focus-primary hover:bg-swdc-silver-300 active:bg-swdc-silver-300 flex origin-center items-center gap-x-0.5 rounded-xs px-1 py-0.5 capitalize outline-2 outline-offset-0 outline-transparent transition duration-100 ease-out focus:not-data-focus:outline-none active:scale-[0.96]",
                      open && "bg-swdc-silver-200",
                    )
                  : cn(
                      "group text-link-m text-swdc-black-600 active:text-swdc-blue-500 relative flex h-full items-center font-medium outline-none",
                      "after:absolute after:right-0 after:-bottom-2 after:left-0 after:rounded-full after:bg-transparent after:content-['']",
                      "hover:after:bg-swdc-silver-400 focus:after:bg-swdc-silver-400 active:after:bg-swdc-silver-400 hover:after:h-0.5",
                      open
                        ? isRedesigned
                          ? "data-focus:bg-swdc-silver-200"
                          : "after:bg-swdc-silver-400 after:h-1 active:after:h-0.5"
                        : "",
                    )
              }
              {...getDataLinkAttributes(dropdown)}
              {...getDataAutomationGroup("dropdown")}
            >
              {dropdown.title}
              <CaretDownSolid
                width={10}
                height={10}
                className={cn(
                  "text-swdc-black-600",
                  isRedesigned
                    ? "ml-0.5 transition-transform duration-100 ease-out"
                    : "group-active:text-swdc-blue-500 ml-2",
                  open && "rotate-180",
                )}
                aria-hidden="true"
              />
            </PopoverButton>
            {isRedesigned ? (
              <PopoverPanel
                className="bg-swdc-cool-gray-200 text-swdc-black-600 absolute top-full left-1/2 z-10 mt-1 max-h-[calc(100vh-10rem)] w-full origin-top -translate-x-1/2 overflow-y-auto shadow-[0_20px_40px_0_rgba(0,0,0,0.2)] transition duration-400 ease-out data-closed:-translate-y-2 data-closed:opacity-0 data-leave:duration-200 data-leave:ease-in-out"
                transition
                {...getDataAutomationGroup("panel")}
                // Keep the panel rendered for better SEO
                unmount={false}
              >
                <div className="swdc-container mx-auto flex flex-col gap-y-8 px-2 pt-4 pb-2">
                  <div className="flex items-stretch">
                    {columns.map((column, i) => (
                      <div
                        key={`${column.title}-${i}`}
                        className="border-swdc-silver-200 flex flex-1 flex-col gap-y-2 px-2 not-last:border-r last:border-r-0"
                      >
                        {hasHeadline && column.title && (
                          <div
                            className="border-b-swdc-silver-200 text-body-xs text-swdc-gray-300 border-b pr-2 pb-2"
                            role="heading"
                            aria-level={3}
                          >
                            {column.title}
                          </div>
                        )}
                        <ul className="flex flex-col">
                          {column.links?.map(
                            (link, index) =>
                              link && (
                                <li key={`${link.title}-${index}`}>
                                  <PopoverButton
                                    as={Link}
                                    href={link.href}
                                    className="group text-body-s text-swdc-black-600 data-focus:outline-swdc-focus-primary flex w-full items-center justify-between rounded-xs p-2 font-bold transition duration-100 ease-out hover:bg-white active:scale-[0.98] data-focus:outline-2"
                                    dld={link.dld}
                                    dlt={link.dlt}
                                  >
                                    <span>{link.title}</span>
                                    <CaretDownSolid
                                      width={10}
                                      height={10}
                                      fill="#F37021"
                                      className="ml-2 -rotate-90 opacity-0 transition-opacity duration-100 ease-out group-hover:opacity-100"
                                      aria-hidden="true"
                                    />
                                  </PopoverButton>
                                </li>
                              ),
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                  {dropdown.rightLink?.title && (
                    <PopoverButton
                      as={Link}
                      href={dropdown.rightLink.href}
                      className="text-link-m text-swdc-black-600 flex items-center gap-x-1 self-start p-2 font-bold underline decoration-1 transition duration-100 ease-out hover:decoration-2"
                      dld={dropdown.rightLink.dld}
                      dlt={dropdown.rightLink.dlt}
                    >
                      {dropdown.rightLink.title}
                      <CaretDownSolid
                        width={10}
                        height={10}
                        fill="#F37021"
                        className="-rotate-90"
                        aria-hidden="true"
                      />
                    </PopoverButton>
                  )}
                </div>
              </PopoverPanel>
            ) : (
              <PopoverPanel
                className="bg-swdc-cool-gray-100 text-swdc-black-600 absolute top-full left-1/2 mx-auto max-h-[calc(100vh-10rem)] w-full -translate-x-1/2 overflow-hidden overflow-y-auto px-4 shadow-[0_20px_40px_0_rgba(0,0,0,0.2)]"
                {...getDataAutomationGroup("panel")}
                // Keep the Popover.Panel rendered for better SEO
                unmount={false}
              >
                <div
                  className={cn(
                    "swdc-container mx-auto flex flex-col gap-y-4 pt-6",
                    columns.length !== 3 ? "pb-8" : "pb-14",
                  )}
                >
                  {dropdown.rightLink?.title && (
                    <div className="w-full text-right">
                      <div className="inline-flex">
                        <Button
                          variant="tertiary"
                          {...getDataLinkAttributes(dropdown.rightLink)}
                          {...{
                            href: dropdown.rightLink.href,
                            icon: "none",
                          }}
                        >
                          {dropdown.rightLink.title}
                        </Button>
                      </div>
                    </div>
                  )}
                  <div
                    className={cn(
                      "grid gap-x-8",
                      columns.length === 3
                        ? "grid-cols-3 [&>div:nth-child(3n)]:max-w-100"
                        : columns.length === 2
                          ? "grid-cols-3 [&>div>ul]:columns-1"
                          : "grid-cols-1",
                    )}
                  >
                    {columns.map((column, i) => (
                      <div
                        key={`${column.title}-${i}`}
                        className={cn(
                          "flex flex-col gap-y-6",
                          columns.length === 1
                            ? "w-full"
                            : columns.length === 2
                              ? "col-span-1 max-w-200 last:col-span-2 last:max-w-100"
                              : "max-w-96",
                        )}
                      >
                        {hasHeadline && (
                          <div
                            className={cn(
                              "border-b-swdc-silver-400 text-subhead-s min-h-10.25 border-b-1 pb-2 font-medium",
                            )}
                            role="heading"
                            aria-level={3}
                          >
                            {column.title}
                          </div>
                        )}
                        <ul
                          className={cn(
                            columns.length === 2
                              ? "max-w-178 columns-2"
                              : columns.length === 1
                                ? "max-w-282 columns-3"
                                : "flex flex-col",
                            "gap-x-30 gap-y-6",
                          )}
                        >
                          {column.links?.map(
                            (link, index) =>
                              link && (
                                <li
                                  key={`${link.title}-${index}`}
                                  className={cn(
                                    columns.length !== 3 &&
                                      "break-inside-avoid pb-6",
                                  )}
                                >
                                  <PopoverButton
                                    as={Link}
                                    className={cn(
                                      "text-link-m hover:underline",
                                    )}
                                    href={link.href}
                                    dld={link.dld}
                                    dlt={link.dlt}
                                  >
                                    {link.title}
                                  </PopoverButton>
                                </li>
                              ),
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverPanel>
            )}
          </>
        );
      }}
    </Popover>
  );
};

export default Dropdown;
